import React, { useMemo, useContext, useCallback } from 'react';
import { Checkbox } from '@fluentui/react';
import classNames from 'classnames';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { SurveyContext } from '@bingads-webui-campaign-react/survey-context';
import { SurveyContextType } from '../../../survey-context';
import { MultipleSelectionData } from '../../../survey.types';
import { Option } from './option';
import { QuestionTitle } from './title';
import { generateLabelId } from '../../../utility';

export const CheckboxGroupView = React.memo((props: {
  configurator: any,
  data: any,
  setData: any,
  dirtyState: any,
  setDirtyState: any,
}) => {
  const {
    configurator,
    data,
    setData,
    dirtyState,
    setDirtyState,
  } = props;

  const classes: any = useDefaultStyles(() => ({
    checkboxGroup: {
      '& .ms-Checkbox ~ .ms-Checkbox': {
        marginTop: '9px',
      },
      marginTop: '4px',
    },
  }));

  const {
    options,
    key: questionKey,
  } = configurator.getConfiguration();

  const error = useMemo(() => configurator.getError(data), [configurator, data]);

  const setOptionData = useCallback((optionKey, value) => {
    const newData = {
      ...data,
      [optionKey]: {
        ...data[optionKey],
        ...value,
      },
    };
    setData(newData);
  }, [data, setData]);

  const mySetDirtyState = useCallback((key, value) => {
    const newDirtyState = {
      ...dirtyState,
      [key]: value,
    };
    setDirtyState(newDirtyState);
  }, [dirtyState, setDirtyState]);

  // @ts-ignore
  const surveyContext : SurveyContextType = useContext(SurveyContext);
  const {
    logActivity,
    getSurveyAnswers,
  } = surveyContext;

  const myLogActivity = useCallback((log: string) => {
    logActivity(`Question: ${configurator.key}, type: multiple-selection, ${log}`);
  }, [configurator.key, logActivity]);

  const answers = getSurveyAnswers();

  const title = useMemo(() => configurator.getTitle(answers), [answers, configurator]);
  const labelKey = useMemo(() => generateLabelId(questionKey), [questionKey]);

  return (
    <div
      className={classNames('checkbox-group', classes.checkboxGroup)}
      aria-label={title}
      aria-labelledby={labelKey}
      role="group"
    >
      {options.map((option) => {
        const {
          key,
          text,
        } = option;

        const {
          selected,
          input,
        } = data[key] ?? {};

        return (<Checkbox
          className="option-checkbox"
          styles={{
            root: {
              display: 'block',
              label: { alignItems: 'flex-start' },
              '& .customized-option': { width: '100%', paddingLeft: '8px' },
              '& input[type="checkbox"]': {
                margin: 'unset',
              },
            },
          }}
          key={key}
          label={text}
          checked={selected}
          onChange={(e, checked) => {
            mySetDirtyState('isDirty', true);
            setOptionData(option.key, { selected: checked });
            myLogActivity(`action: select checkbox, key: ${option.key}, state: ${checked}`);
          }}
          onRenderLabel={() => (
            <Option
              option={option}
              error={error[key]}
              data={input}
              setData={(dataKey, stringInput) => setOptionData(dataKey, { input: stringInput })}
              dirtyState={dirtyState[key]}
              setDirtyState={(dataKey, value) => mySetDirtyState(dataKey, value)}
              isSelected={selected}
              logActivity={myLogActivity}
            >
              <span>{text}</span>
            </Option>
          )}
        />);
      })}
    </div>
  );
});


interface MultipleSelectionViewProps {
  configurator: any;
  data: MultipleSelectionData;
  setData: any;
  rootClassName: string | undefined;
  setDirtyState: any;
  dirtyState: any;
}

export const MultipleSelectionView = (props: MultipleSelectionViewProps) => {
  const {
    configurator,
    data,
    setData,
    rootClassName,
    setDirtyState,
    dirtyState,
  } = props;

  const error = useMemo(() => configurator.isAnswered(data), [configurator, data]);
  const { isDirty } = dirtyState;

  return (
    <div className={rootClassName || 'multiple-selection'} >
      <QuestionTitle isDirty={isDirty} configurator={configurator} error={error} >
        <CheckboxGroupView
          setDirtyState={setDirtyState}
          dirtyState={dirtyState}
          configurator={configurator}
          data={data}
          setData={setData}
        />
      </QuestionTitle>
    </div>
  );
};
